// eslint-disable-next-line jira/restricted/graphql-tag
import gql from 'graphql-tag';
import type { DocumentNode } from 'graphql';
import { isProjectsInJiraM1Enabled } from '@jira/platform__services-native-integration-projects/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal } from '@atlassian/jira-feature-experiments';
import {
	LAYOUT_FRAGMENT,
	LAYOUT_FRAGMENT_WITH_ML,
	BASE_FIELD_FRAGMENT,
	BASE_FIELD_FRAGMENT_WITH_CREATE_CONTEXT_OPTION_ARI,
	PROJECT_FIELD_FRAGMENT_WITHOUT_CLASSIFICATION,
	ISSUE_TYPE_FIELD_FRAGMENT,
	ISSUE_TYPE_FIELD_FRAGMENT_WITH_HIERARCHY,
	ISSUE_TYPE_FIELD_SEARCH_BY_FRAGMENT_WITH_HIERARCHY,
	REQUEST_TYPE_FIELD_FRAGMENT,
	REQUEST_TYPE_FIELD_SEARCH_BY_FRAGMENT,
	TEXT_FIELD_FRAGMENT,
	RICH_TEXT_FIELD_FRAGMENT,
	COMPONENTS_FIELD_FRAGMENT,
	COMPONENTS_FIELD_FRAGMENT_ASYNC_OPTIONS,
	VERSION_FIELD_FRAGMENT,
	VERSION_FIELD_FRAGMENT_ASYNC_OPTIONS,
	MULTI_VERSION_FIELD_FRAGMENT,
	MULTI_VERSION_FIELD_FRAGMENT_ASYNC_OPTIONS,
	PRIORITY_FIELD_FRAGMENT,
	SPRINT_FIELD_FRAGMENT,
	LABEL_FIELD_FRAGMENT,
	SINGLE_USER_PICKER_FIELD_FRAGMENT,
	MULTI_USER_PICKER_FIELD_FRAGMENT,
	PEOPLE_FIELD_FRAGMENT,
	SECURITY_FIELD_FRAGMENT,
	ATTACHMENTS_FIELD_FRAGMENT,
	STORAGE_LIMITS_FRAGMENT,
	ISSUE_RESTRICTION_FIELD_FRAGMENT,
	COLOR_FIELD_FRAGMENT,
	RESOLUTION_FIELD_FRAGMENT,
	SINGLE_SELECT_FIELD_FRAGMENT,
	MULTI_SELECT_FIELD_FRAGMENT,
	NUMBER_FIELD_FRAGMENT,
	URL_FIELD_FRAGMENT,
	EPIC_LINK_FIELD_FRAGMENT,
	DATE_FIELD_FRAGMENT,
	DATE_TIME_FIELD_FRAGMENT,
	CASCADING_SELECT_FIELD_FRAGMENT,
	CHECKBOX_FIELD_FRAGMENT,
	SINGLE_GROUP_PICKER_FIELD_FRAGMENT,
	MULTI_GROUP_PICKER_FIELD_FRAGMENT,
	TEAM_FIELD_FRAGMENT,
	ATLASSIAN_TEAM_FIELD_FRAGMENT,
	RADIO_SELECT_FIELD_FRAGMENT,
	AFFECTED_SERVICES_FIELD_FRAGMENT,
	CMDB_FIELD_FRAGMENT,
	ORGANIZATIONS_FIELD_FRAGMENT,
	CONNECT_TEXT_FIELD_FRAGMENT,
	CONNECT_NUMBER_FIELD_FRAGMENT,
	CONNECT_SINGLE_SELECT_FIELD_FRAGMENT,
	CONNECT_MULTI_SELECT_FIELD_FRAGMENT,
	CONNECT_RICH_TEXT_FIELD_FRAGMENT,
	ASSET_FIELD_FRAGMENT,
	FORGE_STRING_FIELD_FRAGMENT,
	FORGE_NUMBER_FIELD_FRAGMENT,
	FORGE_SINGLE_USER_PICKER_FIELD_FRAGMENT,
	FORGE_MULTI_USER_PICKER_FIELD_FRAGMENT,
	FORGE_GROUP_PICKER_FIELD_FRAGMENT,
	FORGE_MULTI_GROUP_PICKER_FIELD_FRAGMENT,
	FORGE_STRINGS_FIELD_FRAGMENT,
	FORGE_DATETIME_FIELD_FRAGMENT,
	FORGE_OBJECT_FIELD_FRAGMENT,
	TIME_TRACKING_FIELD_FRAGMENT,
	WORK_LOG_FIELD_FRAGMENT,
	JSM_MULTI_USER_PICKER_FIELD_FRAGMENT,
	JSM_PEOPLE_FIELD_FRAGMENT,
	PARENT_LINK_FIELD_FRAGMENT,
	PARENT_FIELD_FRAGMENT,
	PROFORMA_CONFIG_FRAGMENT,
	STATUS_FIELD_FRAGMENT,
	WATCHERS_CONFIG_FRAGMENT,
	DATA_CLASSIFICATION_FIELD_FRAGMENT,
	DATA_CLASSIFICATION_FIELD_FRAGMENT_WITH_ORG_DEFAULT,
	DATA_CLASSIFICATION_FIELD_FRAGMENT_WITH_CLASSIFICATION_CONTROL,
	ISSUE_LINKS_FIELD_FRAGMENT_REFINED,
	ISSUE_LINKS_FIELD_FRAGMENT_WITH_COPY_LINKS_AND_ATTACHMENTS_REFINED,
	ATLAS_PROJECT_CONFIG_FRAGMENT,
} from '../../common/graphql/fragments.tsx';
import type { CreateConfigQueryType } from '../common/types.tsx';

const getDataClassificationFragment = () => {
	if (fg('enable_classification_control_on_issue_experiences')) {
		return DATA_CLASSIFICATION_FIELD_FRAGMENT_WITH_CLASSIFICATION_CONTROL;
	}
	return fg('enable_classification_org_default')
		? DATA_CLASSIFICATION_FIELD_FRAGMENT_WITH_ORG_DEFAULT
		: DATA_CLASSIFICATION_FIELD_FRAGMENT;
};

const getDataClassificationInlineFragment = () => {
	if (fg('enable_classification_control_on_issue_experiences')) {
		return '...DataClassificationFieldFragmentWithClassificationControl';
	}
	return fg('enable_classification_org_default')
		? '...DataClassificationFieldFragmentWithOrgDefault'
		: '...DataClassificationFieldFragment';
};

export const getConsolidatedCreateConfigQuery = ({
	isInContext,
	isSubtask = false,
	withML,
	isCreateLinkedIssue,
	isCreateLinkedIssueFromIssueViewFGOn,
	evaluateMiniMode = false,
	isModernised = false,
}: CreateConfigQueryType): DocumentNode => {
	// Determine the GraphQL operation name.
	// Standard and inContext share the same GIRA endpoints (with InContext suffix).
	// Subtask uses entirely different root fields on the schema.
	// eslint-disable-next-line no-nested-ternary
	const operationName = isSubtask
		? 'JiraGlobalSubtaskIssueCreateModalLoad'
		: isInContext
			? `JiraGlobalIssueCreateInContext${isCreateLinkedIssue && isCreateLinkedIssueFromIssueViewFGOn ? 'WithCLI' : ''}`
			: 'JiraGlobalIssueCreateModalLoad';

	// Root field names differ between subtask and standard/inContext modes.
	const layoutField = isSubtask
		? `jiraSubtaskCreationLayout${withML ? 'WithML' : ''}`
		: `jiraIssueCreateLayout${isInContext ? 'InContext' : ''}${withML ? 'WithML' : ''}`;
	const fieldsField = isSubtask
		? 'jiraSubtaskCreationFields'
		: `jiraIssueCreateFields${isInContext ? 'InContext' : ''}`;
	const servicesField = isSubtask
		? 'jiraSubtaskCreationAdditionalServices'
		: 'jiraIssueCreateAdditionalServices';

	// IssueType fragment: subtask uses flat (no hierarchy, no search);
	// inContext uses hierarchy; standard uses search+hierarchy.
	// eslint-disable-next-line no-nested-ternary
	const issueTypeInlineFragment = isSubtask
		? '...IssueTypeFieldFragment'
		: isInContext
			? '...IssueTypeFieldFragmentWithHierarchy'
			: '...IssueTypeFieldSearchFragmentWithHierarchy';
	// eslint-disable-next-line no-nested-ternary
	const issueTypeFragment = isSubtask
		? ISSUE_TYPE_FIELD_FRAGMENT
		: isInContext
			? ISSUE_TYPE_FIELD_FRAGMENT_WITH_HIERARCHY
			: ISSUE_TYPE_FIELD_SEARCH_BY_FRAGMENT_WITH_HIERARCHY;

	// RequestType: standard uses search variant; inContext and subtask use basic variant.
	const requestTypeInlineFragment =
		isInContext || isSubtask ? '...RequestTypeFieldFragment' : '...RequestTypeFieldSearchFragment';
	const requestTypeFragment =
		isInContext || isSubtask ? REQUEST_TYPE_FIELD_FRAGMENT : REQUEST_TYPE_FIELD_SEARCH_BY_FRAGMENT;

	// IssueLinks: inContext with CLI FG uses copy-links variant; all others use refined.
	const issueLinksInlineFragment =
		isInContext && isCreateLinkedIssueFromIssueViewFGOn
			? '...IssueLinksFieldFragmentWithCopyLinksAndAttachmentsRefined'
			: '...IssueLinksFieldFragmentRefined';
	const issueLinksFragment =
		isInContext && isCreateLinkedIssueFromIssueViewFGOn
			? ISSUE_LINKS_FIELD_FRAGMENT_WITH_COPY_LINKS_AND_ATTACHMENTS_REFINED
			: ISSUE_LINKS_FIELD_FRAGMENT_REFINED;

	// forceLegacyCreateExperience: for inContext and subtask it is gated behind the
	// experiment (needsForceLegacy below). For standard it is selected alongside
	// miniMode/fallbackReason when evaluateMiniMode is true (see the fields block),
	// matching the legacy standard query so the modernised modal can honour the
	// backend-driven legacy fallback (e.g. admin "simple create as default" off).
	const needsForceLegacy =
		(isInContext || isSubtask) && expVal('create_work_item_modernization_exp', 'isEnabled', false);

	return gql`
	query ${operationName}(
		$project: JiraProjectInput
		$issueType: JiraIssueTypeInput
		$requestType: JiraRequestTypeInput
		$defaultValues: JiraIssueFieldsInput
		${!isInContext && !isSubtask ? '$issueTypes: String' : ''}
		${!isInContext && !isSubtask ? '$requestTypes: String' : ''}
		${isInContext ? '$additionalContext: JiraIssueCreateContextInput!' : ''}
		${isSubtask ? '$parentId: JiraParentIdInput!' : ''}
		${isInContext && isCreateLinkedIssueFromIssueViewFGOn ? '$issueCreateType: IssueCreateTypeFilter' : ''}
		${!isInContext && !isSubtask && evaluateMiniMode ? '$evaluateMiniMode: Boolean' : ''}
		$withNumberFormat: Boolean!
		$cloudId: ID!
		$applicationKey: JiraApplicationKey!
		$shouldCheckForStorageLimit: Boolean!
	) {
		${layoutField}(
			layoutFilter: USER_CONFIGURED_LAYOUT
			project: $project
			issueType: $issueType
			${!isSubtask && !isInContext ? 'requestType: $requestType' : ''}
			${isSubtask ? 'parentId: $parentId' : ''}
			${isInContext ? 'additionalContext: $additionalContext' : ''}
			${isInContext && isCreateLinkedIssueFromIssueViewFGOn ? 'issueCreateType: $issueCreateType' : ''}
		) {
			...LayoutFragment
		}
		${fieldsField}(
			fieldsFilter: USER_CONFIGURED_FIELDS
			project: $project
			issueType: $issueType
			requestType: $requestType
			defaultValues: $defaultValues
			${isSubtask ? 'parentId: $parentId' : ''}
			${isInContext ? 'additionalContext: $additionalContext' : ''}
			${isInContext && isCreateLinkedIssueFromIssueViewFGOn ? 'issueCreateType: $issueCreateType' : ''}
			${!isInContext && !isSubtask && evaluateMiniMode ? 'evaluateMiniMode: $evaluateMiniMode' : ''}
		) {
			... on JiraIssueCreateFields {
				${!isInContext && !isSubtask && evaluateMiniMode ? 'miniMode fallbackReason forceLegacyCreateExperience' : ''}
				${needsForceLegacy ? 'forceLegacyCreateExperience' : ''}
				fields {
					nodes {
						${fg('issue_create_reuse_labels_field_for_create') ? '...BaseFieldWithAriFragment' : '...BaseFieldFragment'}
						...ProjectFieldFragmentWithoutClassification
						${issueTypeInlineFragment}
						${requestTypeInlineFragment}
						...TextFieldFragment
						...RichTextFieldFragment
						${isModernised && fg('jiv_22549_modern_wic_components_async_options') ? '...ComponentsFieldFragmentAsyncOptions' : '...ComponentsFieldFragment'}
						${isModernised && fg('jiv_22550_modern_wic_versions_async_options') ? '...VersionFieldFragmentAsyncOptions' : '...VersionFieldFragment'}
						${isModernised && fg('jiv_22550_modern_wic_versions_async_options') ? '...MultiVersionFieldFragmentAsyncOptions' : '...MultiVersionFieldFragment'}
						...PriorityFieldFragment
						...SprintFieldFragment
						...LabelFieldFragment
						...SingleUserPickerFieldFragment
						...MultiUserPickerFieldFragment
						...PeopleFieldFragment
						...SecurityFieldFragment
						...IssueRestrictionFieldFragment
						...ColorFieldFragment
						...ResolutionFieldFragment
						...SingleSelectFieldFragment
						...MultiSelectFieldFragment
						...NumberFieldFragment
						...UrlFieldFragment
						...EpicLinkFieldFragment
						...DatePickerFieldFragment
						...DateTimePickerFieldFragment
						...CascadingSelectFieldFragment
						...AttachmentsFieldFragment
						...CheckboxFieldFragment
						${issueLinksInlineFragment}
						...SingleGroupPickerFieldFragment
						...MultiGroupPickerFieldFragment
						...TeamFieldFragment
						...AtlassianTeamFieldFragment
						...RadioSelectFieldFragment
						...AffectedServicesFieldFragment
						...CMDBFieldFragment
						...OrganizationsFieldFragment
						...ConnectTextFieldFragment
						...ConnectNumberFieldFragment
						...ConnectSingleSelectFieldFragment
						...ConnectMultiSelectFieldFragment
						...ConnectRichTextFieldFragment
						...AssetFieldFragment
						...ForgeStringFieldFragment
						...ForgeNumberFieldFragment
						...ForgeSingleUserPickerFieldFragment
						...ForgeMultiUserPickerFieldFragment
						...ForgeGroupPickerFieldFragment
						...ForgeMultiGroupPickerFieldFragment
						...ForgeStringsFieldFragment
						...ForgeObjectFieldFragment
						...ForgeDateTimePickerFieldFragment
						...TimeTrackingFieldFragment
						...WorkLogFieldFragment
						...JsmMultiUserPickerFieldFragment
						...JsmPeopleFieldFragment
						...ParentLinkFieldFragment
						${!isInContext && !isSubtask ? getDataClassificationInlineFragment() : ''}
						...ParentFieldFragment
						...StatusFieldFragment
						${isProjectsInJiraM1Enabled() ? '...AtlasProjectFieldFragment' : ''}
					}
				}
				errors {
					identifier
					extensions {
						... on JiraIssueFieldUnsupportedErrorExtension {
							fieldName
							fieldType
							isRequiredField
						}
						... on JiraIssueFieldValueValidationErrorExtension {
							errorType
							message
						}
					}
				}
			}
			... on QueryError {
				message
			}
		}
		${servicesField}(
			project: $project
			issueType: $issueType
			requestType: $requestType
			${isSubtask ? 'parentId: $parentId' : ''}
		) {
			... on JiraIssueCreateAdditionalServices {
				services {
					edges {
						node {
							...WatchersConfigFragment
							${!isSubtask ? '...ProformaConfigFragment' : ''}
						}
					}
				}
			}
			... on QueryError {
				message
			}
		}
		jira @include(if: $shouldCheckForStorageLimit) {
			...StorageLimitsFragment
		}
	}
	${withML ? LAYOUT_FRAGMENT_WITH_ML : LAYOUT_FRAGMENT}
	${fg('issue_create_reuse_labels_field_for_create') ? BASE_FIELD_FRAGMENT_WITH_CREATE_CONTEXT_OPTION_ARI : BASE_FIELD_FRAGMENT}
	${PROJECT_FIELD_FRAGMENT_WITHOUT_CLASSIFICATION}
	${issueTypeFragment}
	${requestTypeFragment}
	${TEXT_FIELD_FRAGMENT}
	${RICH_TEXT_FIELD_FRAGMENT}
	${isModernised && fg('jiv_22549_modern_wic_components_async_options') ? COMPONENTS_FIELD_FRAGMENT_ASYNC_OPTIONS : COMPONENTS_FIELD_FRAGMENT}
	${isModernised && fg('jiv_22550_modern_wic_versions_async_options') ? VERSION_FIELD_FRAGMENT_ASYNC_OPTIONS : VERSION_FIELD_FRAGMENT}
	${isModernised && fg('jiv_22550_modern_wic_versions_async_options') ? MULTI_VERSION_FIELD_FRAGMENT_ASYNC_OPTIONS : MULTI_VERSION_FIELD_FRAGMENT}
	${PRIORITY_FIELD_FRAGMENT}
	${SPRINT_FIELD_FRAGMENT}
	${LABEL_FIELD_FRAGMENT}
	${SINGLE_USER_PICKER_FIELD_FRAGMENT}
	${MULTI_USER_PICKER_FIELD_FRAGMENT}
	${SECURITY_FIELD_FRAGMENT}
	${ISSUE_RESTRICTION_FIELD_FRAGMENT}
	${COLOR_FIELD_FRAGMENT}
	${RESOLUTION_FIELD_FRAGMENT}
	${SINGLE_SELECT_FIELD_FRAGMENT}
	${MULTI_SELECT_FIELD_FRAGMENT}
	${NUMBER_FIELD_FRAGMENT}
	${URL_FIELD_FRAGMENT}
	${EPIC_LINK_FIELD_FRAGMENT}
	${DATE_FIELD_FRAGMENT}
	${DATE_TIME_FIELD_FRAGMENT}
	${CASCADING_SELECT_FIELD_FRAGMENT}
	${ATTACHMENTS_FIELD_FRAGMENT}
	${CHECKBOX_FIELD_FRAGMENT}
	${issueLinksFragment}
	${SINGLE_GROUP_PICKER_FIELD_FRAGMENT}
	${MULTI_GROUP_PICKER_FIELD_FRAGMENT}
	${PEOPLE_FIELD_FRAGMENT}
	${TEAM_FIELD_FRAGMENT}
	${ATLASSIAN_TEAM_FIELD_FRAGMENT}
	${RADIO_SELECT_FIELD_FRAGMENT}
	${AFFECTED_SERVICES_FIELD_FRAGMENT}
	${CMDB_FIELD_FRAGMENT}
	${ORGANIZATIONS_FIELD_FRAGMENT}
	${CONNECT_TEXT_FIELD_FRAGMENT}
	${CONNECT_NUMBER_FIELD_FRAGMENT}
	${CONNECT_SINGLE_SELECT_FIELD_FRAGMENT}
	${CONNECT_MULTI_SELECT_FIELD_FRAGMENT}
	${CONNECT_RICH_TEXT_FIELD_FRAGMENT}
	${ASSET_FIELD_FRAGMENT}
	${FORGE_STRING_FIELD_FRAGMENT}
	${FORGE_NUMBER_FIELD_FRAGMENT}
	${FORGE_SINGLE_USER_PICKER_FIELD_FRAGMENT}
	${FORGE_MULTI_USER_PICKER_FIELD_FRAGMENT}
	${FORGE_GROUP_PICKER_FIELD_FRAGMENT}
	${FORGE_MULTI_GROUP_PICKER_FIELD_FRAGMENT}
	${FORGE_STRINGS_FIELD_FRAGMENT}
	${FORGE_DATETIME_FIELD_FRAGMENT}
	${FORGE_OBJECT_FIELD_FRAGMENT}
	${TIME_TRACKING_FIELD_FRAGMENT}
	${WORK_LOG_FIELD_FRAGMENT}
	${JSM_MULTI_USER_PICKER_FIELD_FRAGMENT}
	${JSM_PEOPLE_FIELD_FRAGMENT}
	${PARENT_LINK_FIELD_FRAGMENT}
	${PARENT_FIELD_FRAGMENT}
	${!isSubtask ? PROFORMA_CONFIG_FRAGMENT : ''}
	${STATUS_FIELD_FRAGMENT}
	${WATCHERS_CONFIG_FRAGMENT}
	${!isInContext && !isSubtask ? getDataClassificationFragment() : ''}
	${STORAGE_LIMITS_FRAGMENT}
	${isProjectsInJiraM1Enabled() ? ATLAS_PROJECT_CONFIG_FRAGMENT : ''}
`;
};
